import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AvisoPrimeirasAvaliacoes } from './primeiros/AvisoPrimeirasAvaliacoes';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  LayoutDashboard, 
  Users, 
  Kanban, 
  List, 
  UserPlus, 
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  ClipboardList,
  Map,
  Shield,
  BarChart3,
  Lock,
  Settings,
  Bot,
  DollarSign,
  Package,
  Briefcase,
  Activity,
  Building2,
  MapPin,
  Barcode,
  PackageCheck,
  FileText,
  FolderArchive,
  BedDouble, Stethoscope } from 'lucide-react';
import { PROFILE_LABELS } from '../lib/utils';
import { ROUTE_PERMS } from '../lib/routePermissions';
import NotificationBell from './NotificationBell';
import RealTimeAlerts from './RealTimeAlerts';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Kanban', href: '/kanban', icon: Kanban },
  { name: 'Internações', href: '/lista', icon: List },
  { name: 'Tarefas', href: '/tarefas', icon: ClipboardList },
  { name: 'Prescrição', href: '/prescricao', icon: ClipboardList },
  { name: 'Dispensação', href: '/dispensacao', icon: ClipboardList },
  { name: 'Check-in Pacientes', href: '/visitas', icon: MapPin },
  { name: 'Controle de Guias', href: '/guias', icon: FileText },
  { name: 'Recebimento de Guias', href: '/recebimento-guias', icon: PackageCheck },
  { name: 'Fechamento Mensal', href: '/fechamento-mensal', icon: FolderArchive },
  { name: 'Primeiras Avaliações', href: '/primeiros-atendimentos', icon: Stethoscope },
  { name: 'Diárias de Internação', href: '/diarias', icon: BedDouble },
  { name: 'Estatísticas', href: '/estatisticas', icon: BarChart3 },
  { name: 'Custos', href: '/custos', icon: DollarSign },
  { name: 'Auditoria Financeira', href: '/auditoria-financeira', icon: DollarSign },
  { name: 'Auditoria de Prontuários', href: '/auditoria-prontuarios', icon: Shield },
  { name: 'Estoque', href: '/estoque', icon: Package },
  { name: 'Controle SKU', href: '/sku', icon: Barcode },
  { name: 'Aprovisionamento', href: '/aprovisionamento', icon: PackageCheck },
  { name: 'Comodato', href: '/comodato', icon: Package },
  { name: 'Especialidades', href: '/fisioterapia', icon: Activity },
  { name: 'Relatórios', href: '/relatorios', icon: BarChart3 },
  { name: 'Serviços', href: '/servicos', icon: Briefcase },
  { name: 'Mostrar no Mapa', href: '/mapa', icon: Map },
  { name: 'Pacientes', href: '/pacientes', icon: Users },
];

// AI Agent special item (highlighted)
const aiAgentNav = { name: 'IA Business Intelligence', href: '/agente-ia', icon: Bot };

// Metrics dashboard (highlighted)
const metricsDashboardNav = { name: 'Métricas em Tempo Real', href: '/metricas', icon: Activity };

// Integration with external EMR
const medicinaDiretaNav = { name: 'Medicina Direta', href: '/medicina-direta', icon: Building2 };

// Master Admin only navigation
const masterAdminNav = [
  { name: 'Admin de Segurança', href: '/admin-seguranca', icon: Shield },
];

const Layout = ({ children }) => {
  const { user, logout, can } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-card border-r border-border
        transform transition-transform duration-200 ease-in-out flex flex-col
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border flex-shrink-0">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src="/assets/logo-symbol.png" alt="LIFEPORT HOME" className="h-9 w-9 object-contain" />
            <div className="flex flex-col">
              <span className="font-bold text-lg font-['Manrope'] text-[hsl(var(--lifeport-red))]">LIFEPORT HOME</span>
            </div>
          </Link>
          <button 
            className="lg:hidden p-2 hover:bg-muted rounded-md"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="p-4 space-y-1 flex-1 overflow-y-auto pb-4">
          {navigation
            .filter((item) => {
              const required = ROUTE_PERMS[item.href];
              return !required || required.length === 0 || can(...required);
            })
            .map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href === '/lista' && location.pathname.startsWith('/lista'));
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium
                  transition-all duration-200
                  ${isActive 
                    ? 'bg-accent text-accent-foreground shadow-sm' 
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }
                `}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
          
          {/* AI Agent - Highlighted Special Item - Only shown if user has permission */}
          {can('usar_agente_ia') && (
            <Link
              to={aiAgentNav.href}
              onClick={() => setSidebarOpen(false)}
              className={`
                flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium
                transition-all duration-200 mt-2
                ${location.pathname === aiAgentNav.href
                  ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg shadow-emerald-500/30' 
                  : 'bg-gradient-to-r from-emerald-500/10 to-green-600/10 text-emerald-700 dark:text-emerald-400 hover:from-emerald-500/20 hover:to-green-600/20 border border-emerald-500/30'
                }
              `}
              data-testid="nav-ia-business-intelligence"
            >
              <div className={`p-1 rounded ${location.pathname === aiAgentNav.href ? 'bg-white/20' : 'bg-emerald-500/20'}`}>
                <aiAgentNav.icon className="h-4 w-4" />
              </div>
              <span className="flex-1 truncate">{aiAgentNav.name}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${location.pathname === aiAgentNav.href ? 'bg-white/20' : 'bg-emerald-500/20'}`}>
                IA
              </span>
            </Link>
          )}

          {/* Metrics Dashboard - Highlighted Special Item */}
          {can('ver_metricas') && (
            <Link
              to={metricsDashboardNav.href}
              onClick={() => setSidebarOpen(false)}
              className={`
                flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium
                transition-all duration-200 mt-2
                ${location.pathname === metricsDashboardNav.href
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-lg shadow-blue-500/30' 
                  : 'bg-gradient-to-r from-blue-500/10 to-cyan-600/10 text-blue-700 dark:text-blue-400 hover:from-blue-500/20 hover:to-cyan-600/20 border border-blue-500/30'
                }
              `}
              data-testid="nav-metricas-tempo-real"
            >
              <div className={`p-1 rounded ${location.pathname === metricsDashboardNav.href ? 'bg-white/20' : 'bg-blue-500/20'}`}>
                <metricsDashboardNav.icon className="h-4 w-4" />
              </div>
              <span className="flex-1 truncate">{metricsDashboardNav.name}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 animate-pulse ${location.pathname === metricsDashboardNav.href ? 'bg-white/20' : 'bg-blue-500/20'}`}>
                LIVE
              </span>
            </Link>
          )}

          {/* Medicina Direta Integration */}
          {can('ver_medicina_direta') && (
            <Link
              to={medicinaDiretaNav.href}
              onClick={() => setSidebarOpen(false)}
              className={`
                flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium
                transition-all duration-200 mt-2 mb-2
                ${location.pathname === medicinaDiretaNav.href
                  ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg shadow-purple-500/30' 
                  : 'bg-gradient-to-r from-purple-500/10 to-pink-600/10 text-purple-700 dark:text-purple-400 hover:from-purple-500/20 hover:to-pink-600/20 border border-purple-500/30'
                }
              `}
              data-testid="nav-medicina-direta"
            >
              <div className={`p-1 rounded ${location.pathname === medicinaDiretaNav.href ? 'bg-white/20' : 'bg-purple-500/20'}`}>
                <medicinaDiretaNav.icon className="h-4 w-4" />
              </div>
              <span className="flex-1 truncate">{medicinaDiretaNav.name}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${location.pathname === medicinaDiretaNav.href ? 'bg-white/20' : 'bg-purple-500/20'}`}>
                EMR
              </span>
            </Link>
          )}
          
          {/* Master Admin Only Navigation */}
          {(user?.perfil || '').toLowerCase() === 'master_admin' && (
            <>
              <div className="pt-4 pb-2">
                <p className="px-4 text-xs font-semibold text-red-600 uppercase tracking-wider">
                  Master Admin
                </p>
              </div>
              {masterAdminNav.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium
                      transition-all duration-200
                      ${isActive 
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 shadow-sm' 
                        : 'text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20'
                      }
                    `}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.name}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* Quick Actions */}
        <div className="p-4 border-t border-border flex-shrink-0">
          {can('criar_pacientes') && (
          <Link to="/pacientes/novo" onClick={() => setSidebarOpen(false)}>
            <Button className="w-full gap-2" data-testid="btn-novo-paciente-sidebar">
              <UserPlus className="h-4 w-4" />
              Novo Paciente
            </Button>
          </Link>
          )}
        </div>

        {/* User Info */}
        <div className="p-4 border-t border-border bg-card flex-shrink-0">
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-accent text-accent-foreground text-sm">
                {user?.nome ? getInitials(user.nome) : 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.nome}</p>
              <p className="text-xs text-muted-foreground">{PROFILE_LABELS[user?.perfil]}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="h-16 bg-card border-b border-border sticky top-0 z-30">
          <div className="h-full px-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                className="lg:hidden p-2 hover:bg-muted rounded-md"
                onClick={() => setSidebarOpen(true)}
                data-testid="btn-mobile-menu"
              >
                <Menu className="h-5 w-5" />
              </button>

              {/* Logo + Title in header */}
              <Link to="/dashboard" className="flex items-center gap-2">
                <img 
                  src="/assets/lifeport-simbolo.png" 
                  alt="Logo Lifeport" 
                  className="h-8 w-8 object-contain"
                />
                <div className="hidden min-[420px]:flex flex-col">
                  <span className="font-bold text-base font-['Manrope'] text-[hsl(var(--lifeport-red))] whitespace-nowrap">Lifeport Home</span>
                  <span className="text-[10px] text-muted-foreground leading-none">Saúde no Lar</span>
                </div>
              </Link>
            </div>

            <div className="flex-1" />

            <div className="flex items-center gap-2">
              {/* Real-time Alerts */}
              <RealTimeAlerts />
              
              {/* Notifications */}
              <NotificationBell />
              
              {/* Theme toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="btn-theme-toggle"
              >
                {theme === 'dark' ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="btn-user-menu">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-accent text-accent-foreground text-xs">
                        {user?.nome ? getInitials(user.nome) : 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:block text-sm">{user?.nome}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.nome}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/configuracoes')} data-testid="btn-configuracoes">
                    <Settings className="h-4 w-4 mr-2" />
                    Configurações
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/alterar-senha')} data-testid="btn-alterar-senha">
                    <Lock className="h-4 w-4 mr-2" />
                    Alterar Senha
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="btn-logout">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <AvisoPrimeirasAvaliacoes />
        {/* Page content */}
        <main className="lp-main p-3 sm:p-6 page-enter">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
